import { isDefined } from "@clipboard-health/util-ts";
import { type DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useCreateOrGetPlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import {
  type UpdatePlacementCandidateParams,
  useUpdatePlacementCandidate,
} from "@src/appV2/redesign/PlacementCandidate/api/useUpdatePlacementCandidate";
import type {
  PlacementCandidate,
  PreferredCandidateArea,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useState } from "react";

import {
  type GetPlacementsForWorkplaceQuery,
  placementsFilterSchema,
} from "./types/fetchPlacements.schema";

/**
 * Convert a placement candidate's stored preferences into the filter shape
 * consumed by the placements list.
 */
function candidateToFilter(
  candidate: PlacementCandidate
): GetPlacementsForWorkplaceQuery["filter"] {
  const filter: GetPlacementsForWorkplaceQuery["filter"] = {};

  if (candidate.preferredAreas?.length) {
    // We only support distance filter in UI for now. Use the first preferred area.
    const [{ distance }] = candidate.preferredAreas;

    if (distance > 0) {
      filter.distanceInMiles = { $lt: distance };
    }
  }

  if (candidate.preferredDaysOfWeek?.length) {
    filter.daysOfWeek = candidate.preferredDaysOfWeek.join(",");
  }

  if (candidate.preferredStates?.length) {
    filter.state = candidate.preferredStates.join(",");
  }

  return filter;
}

/**
 * Convert UI filter back to placement candidate update params.
 * Only touches the fields we currently surface in the UI.
 */
function filterToCandidateUpdate(
  filter: GetPlacementsForWorkplaceQuery["filter"],
  previousCandidate: PlacementCandidate
) {
  const params: UpdatePlacementCandidateParams = {
    placementCandidateId: previousCandidate.id,
    workerId: previousCandidate.worker.id,
  };

  if (!filter) {
    return params;
  }

  const distanceObject = filter.distanceInMiles;
  const distance = distanceObject?.$lt ?? 0;
  const preferredAreas: PreferredCandidateArea[] | undefined = previousCandidate.preferredAreas?.[0]
    ? [
        {
          ...previousCandidate.preferredAreas[0],
          distance,
        },
      ]
    : undefined;
  params.preferredAreas = preferredAreas;

  if (filter.daysOfWeek) {
    params.preferredDaysOfWeek = filter.daysOfWeek.split(",") as DayOfTheWeek[];
  }

  if (filter.state) {
    params.preferredStates = filter.state.split(",");
  }

  return params;
}

export function usePlacementsFilter() {
  const worker = useDefinedWorker();

  // Persist workerTypes in localStorage since backend doesn't support it
  const [storedWorkerTypes, setStoredWorkerTypes] = useLocalStorage<string | undefined>(
    "jobs-filter-worker-types",
    undefined
  );

  const { data: placementCandidate, refetch: refetchPlacementCandidate } =
    useCreateOrGetPlacementCandidate(
      { workerId: worker.userId },
      { enabled: isDefined(worker.userId) }
    );

  const { mutate: updateCandidate } = useUpdatePlacementCandidate();

  const [filterState, setFilterState] = useState<GetPlacementsForWorkplaceQuery["filter"]>({});

  // When the placement candidate or stored workerTypes change, sync local state
  useEffect(() => {
    if (!placementCandidate) {
      return;
    }

    const backendFilter: GetPlacementsForWorkplaceQuery["filter"] =
      candidateToFilter(placementCandidate);

    setFilterState((currentFilter) => {
      const mergedFilter: GetPlacementsForWorkplaceQuery["filter"] = {
        ...currentFilter,
        ...backendFilter,
      };

      return mergedFilter;
    });
  }, [placementCandidate]);

  useEffect(() => {
    setFilterState((currentFilter) => {
      if (!isDefined(storedWorkerTypes)) {
        const { workerTypes: _workerTypes, ...rest } = currentFilter ?? {};
        return rest;
      }
      return {
        ...currentFilter,
        workerTypes: storedWorkerTypes,
      };
    });
  }, [storedWorkerTypes]);

  // Wrapper around setFilter that also sends the preference update to backend.
  const setFilter = useCallback(
    (newFilter: GetPlacementsForWorkplaceQuery["filter"]) => {
      const validation = placementsFilterSchema.safeParse(newFilter);
      if (!validation.success) {
        // Invalid filter shape – do nothing.
        return;
      }

      // Persist/remove workerTypes separately in localStorage
      if (Object.hasOwn(newFilter ?? {}, "workerTypes")) {
        if (newFilter?.workerTypes) {
          setStoredWorkerTypes(newFilter.workerTypes);
        } else {
          setStoredWorkerTypes(undefined);
        }
      }

      setFilterState(newFilter);

      if (placementCandidate) {
        const params = filterToCandidateUpdate(newFilter, placementCandidate);
        updateCandidate(params, {
          onSuccess: () => {
            void refetchPlacementCandidate();
          },
        });
      }
    },
    [placementCandidate, updateCandidate, setStoredWorkerTypes, refetchPlacementCandidate]
  );

  return [filterState, setFilter] as const;
}
